<template>
  <div class="container v-content-container popup-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cube"></i>
          <span v-if="selectedInventoryProductId != null">
            {{
              `Inventory Product${
                noDataExist ? "" : ": " + selectedInventoryProduct.name
              }`
            }}
          </span>
          <span v-else-if="!loading">
            {{
              $label("inventory.new_inventory_product", "New inventory product")
            }}
          </span>
        </h1>
      </div>
    </div>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" id="inventory-item-form">
        <section>
          <div class="row">
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-bars"></i>
                {{ $label("common.general", "General") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Inventory product name -->
                      <section class="col col-8">
                        <text-input
                          v-model="selectedInventoryProduct.name"
                          id="inventory-item-name"
                          :label="$label('inventory.name', 'Name')"
                          :placeholder="$label('inventory.name', 'Name')"
                          :is-disabled="updating"
                          :has-error="hasError('name')"
                          :error="getError('name')"
                          @keyup.native.enter="createOrUpdateInventoryProduct"
                        />
                      </section>
                      <!-- END Inventory product name -->
                      <!-- BEGIN Inventory product short name -->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedInventoryProduct.short_name"
                          id="inventory-item-short-name"
                          :label="$label('inventory.short_name', 'Short name')"
                          :placeholder="
                            $label('inventory.short_name', 'Short name')
                          "
                          :is-disabled="updating"
                          :has-error="hasError('short_name')"
                          :error="getError('short_name')"
                          @keyup.native.enter="createOrUpdateInventoryProduct"
                        />
                      </section>
                      <!-- END Inventory product short name -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Inventory product amount -->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedInventoryProduct.max_available_units"
                          id="inventory-item-amount"
                          :label="$label('inventory.amount', 'Amount')"
                          :placeholder="$label('inventory.amount', 'Amount')"
                          :is-disabled="updating"
                          :has-error="hasError('max_available_units')"
                          :error="getError('max_available_units')"
                          @keyup.native.enter="createOrUpdateInventoryProduct"
                        />
                      </section>
                      <!-- END Inventory product amount -->
                      <section class="col col-4"></section>
                      <!-- BEGIN Inventory product price type -->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("inventory.price_type", "Price type") }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-price-type"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.billing_type"
                          >
                            <option value="fixed">Fixed</option>
                            <option value="hourly">Hourly</option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('billing_type')" class="text-danger">
                          {{ getError("billing_type") }}
                        </p>
                      </section>
                      <!-- END Inventory product price type -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Inventory product input -->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("inventory.input", "input") }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-price-form"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.form"
                          >
                            <option value="net">Net</option>
                            <option value="gross">Gross</option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('form')" class="text-danger">
                          {{ getError("form") }}
                        </p>
                      </section>
                      <!-- END Inventory product input -->
                      <!-- BEGIN Inventory product VAT -->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("inventory.vat", "VAT") }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-vat"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.vat_id"
                          >
                            <option
                              v-for="vat in availableVats"
                              :value="vat.id"
                              :key="vat.id"
                              >{{ vat.name }}</option
                            >
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('vat_id')" class="text-danger">
                          {{ getError("vat_id") }}
                        </p>
                      </section>
                      <!-- END Inventory product VAT -->
                      <!-- BEGIN Inventory product price -->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedInventoryProduct.amount"
                          id="inventory-item-price"
                          :label="$label('inventory.price', 'Price')"
                          :placeholder="$label('inventory.price', 'Price')"
                          :is-disabled="updating"
                          :has-error="hasError('amount')"
                          :error="getError('amount')"
                          @keyup.native.enter="createOrUpdateInventoryProduct"
                        />
                      </section>
                      <!-- END Inventory product price -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-edit"></i>
                {{ $label("inventory.description", "Description") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Inventory product description -->
                      <section class="col col-12">
                        <label class="label">
                          {{ $label("inventory.description", "Description") }}
                        </label>
                        <quill-editor
                          class="quill-editor"
                          v-model="selectedInventoryProduct.description"
                          ref="inventoryProductDescriptionEditor"
                          :options="editorOptions"
                        >
                        </quill-editor>

                        <p v-if="hasError('description')" class="text-danger">
                          {{ getError("description") }}
                        </p>
                      </section>
                      <!-- END Inventory product description -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-cog"></i>
                {{ $label("inventory.settings", "Settings") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Inventory product accounting code -->
                      <section class="col col-6">
                        <text-input
                          v-model="selectedInventoryProduct.accounting_code"
                          id="inventory-item-accounting-code"
                          :label="$label('inventory.code', 'Code')"
                          :placeholder="$label('inventory.code', 'Code')"
                          :is-disabled="updating"
                          :has-error="hasError('accounting_code')"
                          :error="getError('accounting_code')"
                          @keyup.native.enter="createOrUpdateInventoryProduct"
                        />
                      </section>
                      <!-- END Inventory product accounting code -->
                      <!-- BEGIN Inventory product bookable online -->
                      <section class="col col-6">
                        <label class="label">{{
                          $label(".bookable_online", "Bookable online")
                        }}</label>
                        <label class="select">
                          <select
                            v-model="selectedInventoryProduct.bookable_online"
                            id="inventory-item-bookable-online"
                          >
                            <option :value="true">{{
                              $label("common.yes", "Yes")
                            }}</option>
                            <option :value="false">{{
                              $label("common.no", "No")
                            }}</option>
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <!-- END Inventory product bookable online -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-sliders"></i>
                {{ $label("inventory.time_limit", "Time limit") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Inventory product lead time -->
                      <section class="col col-6">
                        <label class="label">
                          {{ $label("inventory.lead_time", "Lead time") }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-lead-time"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.lead_mins"
                          >
                            <option value="0">0</option>
                            <option value="1">1 minute</option>
                            <option value="2">2 minutes</option>
                            <option value="3">3 minutes</option>
                            <option value="4">4 minutes</option>
                            <option value="5">5 minutes</option>
                            <option value="6">6 minutes</option>
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <!-- END Inventory product lead time -->
                      <!-- BEGIN Inventory product follow up time -->
                      <section class="col col-6">
                        <label class="label">
                          {{
                            $label("inventory.follow_up_time", "Follow up time")
                          }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-follow-up-time"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.follow_up_mins"
                          >
                            <option value="0">0</option>
                            <option value="1">1 minute</option>
                            <option value="2">2 minutes</option>
                            <option value="3">3 minutes</option>
                            <option value="4">4 minutes</option>
                            <option value="5">5 minutes</option>
                            <option value="6">6 minutes</option>
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <!-- END Inventory product follow up time -->
                      <!-- BEGIN Inventory product minimum booking time -->
                      <section class="col col-6">
                        <label class="label">
                          {{
                            $label(
                              "inventory.minimum_booking_time",
                              "Minimum booking time"
                            )
                          }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-minimum-booking-time"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.min_booking_mins"
                          >
                            <option value="0">0</option>
                            <option value="1">1 minute</option>
                            <option value="2">2 minutes</option>
                            <option value="3">3 minutes</option>
                            <option value="4">4 minutes</option>
                            <option value="5">5 minutes</option>
                            <option value="6">6 minutes</option>
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <!-- END Inventory product minimum booking time -->
                      <!-- BEGIN Inventory product maximum booking time -->
                      <section class="col col-6">
                        <label class="label">
                          {{
                            $label(
                              "inventory.maximum_booking_time",
                              "Maximum booking time"
                            )
                          }}
                        </label>

                        <label class="select">
                          <select
                            id="inventory-item-maximum-booking-time"
                            :disabled="updating"
                            v-model="selectedInventoryProduct.max_booking_mins"
                          >
                            <option value="0">0</option>
                            <option value="1">1 minute</option>
                            <option value="2">2 minutes</option>
                            <option value="3">3 minutes</option>
                            <option value="4">4 minutes</option>
                            <option value="5">5 minutes</option>
                            <option value="6">6 minutes</option>
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <!-- END Inventory product maximum booking time -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-dot-circle-o"></i>
                {{ $label("inventory.locations", "Locations") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Inventory product locations -->
                      <section class="col col-12">
                        <label class="label typo__label">{{
                          $label("inventory.locations", "Locations")
                        }}</label>
                        <multiselect
                          v-model="locationValue"
                          tag-placeholder=""
                          placeholder="Search or add location"
                          label="name"
                          track-by="id"
                          :options="locationOptions"
                          class="v-multiselect v-form"
                          :multiple="true"
                          :taggable="false"
                          :show-labels="false"
                          @tag="addLocation"
                        >
                        </multiselect>
                      </section>
                      <!-- END Inventory product locations -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="InventoryProducts"
            :class="{ 'is-disabled': updating }"
            id="back-inventory-product-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-inventory-product-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateInventoryProduct"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedInventoryProductId == null
            }"
            id="delete-inventory-product-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteInventoryProduct"
          >
          </danger-button>
        </div>
        <div class="row mb-4">
          <div class="col-xs-12">&nbsp;</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api";
import FormErrorsMixin from "@/mixins/FormErrors";
import Multiselect from "vue-multiselect";

import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";
import swal from "sweetalert";

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";

export default {
  name: "InventoryProductDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    quillEditor,
    Multiselect,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      availableVats: null,
      locationValue: [],
      locationOptions: [],
      selectedInventoryProduct: {
        name: null,
        short_name: null,
        description: null,
        bookable_online: false,
        min_booking_mins: 0,
        max_booking_mins: 0,
        lead_mins: 0,
        follow_up_mins: 0,
        vat_id: 1,
        amount: null,
        form: "gross",
        billing_type: "fixed",
        max_available_units: 0,
        accounting_code: ""
      },
      selectedInventoryProductId: null,
      validatedInputs: [
        "name",
        "short_name",
        "description",
        "vat_id",
        "amount",
        "form",
        "billing_type",
        "accounting_code",
        "max_available_units"
      ],
      editorOptions: {
        modules: {
          toolbar: [
            [{ size: ["small", false, "large", "huge"] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ color: [] }]
          ]
        },
        placeholder: ""
      }
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedInventoryProductId = vm.$route.params.inventory_product_id;

      // Get Inventory
      if (
        typeof vm.selectedInventoryProductId !== "undefined" &&
        vm.selectedInventoryProductId !== null
      ) {
        if (vm.$route.query.inventory_product_created) {
          swal("Success!", "The inventory was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getInventoryProduct(() => {
          //
        });
      }

      // Get Available vats
      vm.getVats(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    // Get Inventory
    if (
      typeof this.selectedInventoryProductId !== "undefined" &&
      this.selectedInventoryProductId !== null
    ) {
      this.getInventoryProduct(() => {
        //
      });
    }

    // Get Available vats
    this.getVats(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    addLocation(location) {
      this.locationValue.push(location);
    },

    createOrUpdateInventoryProduct() {
      if (this.selectedInventoryProductId == null) {
        this.createInventoryProduct();
      } else {
        this.updateInventoryProduct();
      }
    },

    createInventoryProduct() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("inventory-products");
      if (this.locationValue && this.locationValue.length) {
        this.selectedInventoryProduct.facility_ids = this.locationValue.map(
          facility => facility.id
        );
      }

      Api()
        .post(endpoint, this.selectedInventoryProduct)
        .then(response => {
          this.selectedInventoryProductId = response.data.data.id;
          this.$router.push({
            name: "InventoryProductDetails",
            params: {
              inventory_product_id: this.selectedInventoryProductId
            },
            query: { inventory_product_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getInventoryProduct() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "inventory-products/" + this.selectedInventoryProductId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedInventoryProduct = response.data.data;
          if (response.data.data.price) {
            if (response.data.data.price.vat) {
              this.selectedInventoryProduct.vat_id =
                response.data.data.price.vat.id;
            }
            this.selectedInventoryProduct.form = response.data.data.price.form;
            this.selectedInventoryProduct.amount =
              response.data.data.price.form === "net"
                ? response.data.data.price.amount_net
                : response.data.data.price.amount_gross;
            this.selectedInventoryProduct.billing_type =
              response.data.data.price.billing_type;
          }
          if (
            response.data.data.facilities &&
            response.data.data.facilities.length
          ) {
            this.selectedInventoryProduct.facility_ids = response.data.data.facilities.map(
              facility => facility.id
            );
            this.locationValue = response.data.data.facilities.map(
              facility => ({
                id: facility.id,
                name: facility.name
              })
            );
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateInventoryProduct() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "inventory-products/" + this.selectedInventoryProductId
      );
      if (this.locationValue && this.locationValue.length) {
        this.selectedInventoryProduct.facility_ids = this.locationValue.map(
          facility => facility.id
        );
      }

      Api()
        .put(endpoint, this.selectedInventoryProduct)
        .then(() => {
          this.getInventoryProduct();
          swal({
            title: "Success!",
            text: "The inventory product updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteInventoryProduct() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The inventory product will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "inventory-products/" + this.selectedInventoryProductId
          );

          Api()
            .delete(endpoint)
            .then(response => {
              console.log(response.status);
              this.$router.push({
                name: "InventoryProducts",
                query: { inventory_product_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete inventory products.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getVats() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("vats");

      Api()
        .get(endpoint)
        .then(response => {
          this.availableVats = response.data.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  computed: {
    editor() {
      return this.$refs.instructorDescriptionEditor.quill;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-6 {
  margin-top: 1.8rem;
}

.quill-editor {
  height: 300px;
  display: flex;
  flex-direction: column;
}
</style>
